<template>
    <div class="role-play-form-container">
        <form class="bg-white px-4 py-7 rounded-xl flex flex-col gap-10">
            <div class="grid gap-7 role-play-grid">
                <div class="flex flex-col gap-7">
                    <FormElement title="Name" question="Who are you roleplaying with?" required>
                        <input
                            v-model="form.name"
                            placeholder="e.g. Alex"
                            aria-required="true"
                            class="body-small font-normal focus-visible:outline-hidden rounded-lg p-2 w-full border border-valence-grey-300 fs-mask placeholder:italic placeholder:text-valence-grey-400" />
                    </FormElement>
                    <FormElement title="Relationship" question="Who are they to you?" required>
                        <input
                            v-model="form.relationship"
                            placeholder="e.g. My manager"
                            aria-required="true"
                            class="body-small font-normal focus-visible:outline-hidden rounded-lg p-2 w-full border border-valence-grey-300 fs-mask placeholder:italic placeholder:text-valence-grey-400" />
                    </FormElement>
                    <FormElement title="Personality" question="How does this person typically behave in interactions with you?" required>
                        <input
                            v-model="form.personality"
                            placeholder="e.g. Emotional, calm, conflict avoidant"
                            aria-required="true"
                            class="body-small font-normal focus-visible:outline-hidden rounded-lg p-2 w-full border border-valence-grey-300 fs-mask placeholder:italic placeholder:text-valence-grey-400" />
                    </FormElement>
                </div>
                <div>
                    <FormElement title="Context" question="What would you like to discuss?" required>
                        <div class="border border-valence-grey-300 rounded-lg p-2 flex flex-col">
                            <BaseTextarea
                                v-model:input="form.context"
                                rows="10"
                                class="font-normal placeholder:italic placeholder:text-valence-grey-400"
                                placeholder="e.g. I need to role play a conversation with my manager where I ask her to be more communicative about her expectations from me"
                                aria-label="Context"
                                aria-required="true"
                                @focus="fromRecording = false" />
                            <button
                                v-if="!isTranscribing && recordingEnabled"
                                class="bi text-2xl/none self-end"
                                :class="[isRecording ? 'bi-stop-circle text-red-500' : 'bi-mic']"
                                type="button"
                                @click.prevent.stop="handleRecord" />
                            <LoadingSpinner v-else-if="recordingEnabled" class="w-6 h-6 self-end" />
                        </div>
                        <p v-if="error" class="text-red-600 details font-medium my-2">{{ error }}</p>
                        <BaseNotification v-if="liveChatError" theme="error" :text="liveChatError" hide-dismiss class="my-2" />
                        <Transition name="fade" mode="out-in">
                            <ContextMeter
                                v-if="isRecording"
                                class="mt-4"
                                :message-stack="messageStack"
                                :checkpoints="checkpointsVoiceMs"
                                :running="isRecording && !isBelowSilenceThreshold" />
                            <ContextMeterText
                                v-else-if="form.context && !fromRecording"
                                class="mt-4"
                                :text="form.context"
                                :message-stack="messageStack"
                                :checkpoints="checkpointsTextChars" />
                        </Transition>
                    </FormElement>
                </div>
            </div>
            <div class="flex flex-col gap-3 md:items-end">
                <p v-if="missingFields.length" class="body-small font-normal text-valence-grey-750 md:text-right" aria-live="polite" data-testid="role-play-setup-missing-fields">
                    Fill in {{ formattedMissingFields }} to start your role play.
                </p>
                <div class="self-stretch md:self-end flex justify-center md:justify-end gap-4">
                    <BaseButton
                        data-testid="role-play-setup-text-chat"
                        type="submit"
                        :disabled="!isFormValid || isFormSubmitting"
                        theme="tertiary"
                        class="border border-valence-grey-300 rounded-3xl flex-1 md:flex-initial"
                        @click.prevent="handleStartRoleplay">
                        <template v-if="isFormSubmitting && isTextChat">
                            <div class="flex items-center">
                                <LoadingSpinner class="w-5 h-5 mr-2" />
                                <span>Submitting...</span>
                            </div>
                        </template>
                        <template v-else>
                            <div class="flex items-center gap-2"><i class="bi bi-keyboard text-2xl/none" aria-hidden="true"></i> Text Chat</div>
                        </template>
                    </BaseButton>
                    <BaseButton
                        v-if="organizationDetails.is_realtime_chat_enabled && recordingEnabled"
                        type="submit"
                        :disabled="!isFormValid || isFormSubmitting"
                        theme="primary"
                        class="rounded-3xl flex-1 md:flex-initial"
                        @click.prevent="handleStartRoleplayLiveChat">
                        <template v-if="isFormSubmitting && !isTextChat">
                            <div class="flex items-center">
                                <LoadingSpinner class="w-5 h-5 mr-2" />
                                <span>Submitting...</span>
                            </div>
                        </template>
                        <template v-else>
                            <div class="flex items-center gap-2"><CoachingModeMarble class="w-6 h-6" /> Live Chat</div>
                        </template>
                    </BaseButton>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { AudioRecorder } from "/js/AudioRecorder";
import { getCookie } from "/js/utils";
import { router } from "@inertiajs/vue3";
import { app } from "@microsoft/teams-js";
import { computedAsync } from "@vueuse/core";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import FormElement from "~vue/components/form/FormElement.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { useRecorder } from "~vue/composables/useRecorder";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import ContextMeter from "~vue/Onboarding/ContextMeter.vue";
import ContextMeterText from "~vue/Onboarding/ContextMeterText.vue";
import { isInIFrame } from "~vue/utils";
import { logError } from "~vue/utils/logUtils";
import { computed, inject, ref, watch } from "vue";

const organizationDetails = inject("organization_details");
const userDetails = inject("user_details");
const coachingSessionId = inject("coachingSessionId", null);

const props = defineProps({
    inChat: { type: Boolean, required: false },
    errors: {
        type: Object,
        default: () => ({}),
    },
    initialData: {
        type: Object,
        default: () => ({
            name: "",
            relationship: "",
            personality: "",
            context: "",
        }),
    },
});

const emit = defineEmits(["inChatRolePlayStarted"]);

const form = ref({
    name: props.initialData.name,
    relationship: props.initialData.relationship,
    personality: props.initialData.personality,
    context: props.initialData.context,
});

const fromRecording = ref(false);

const requiredFields = [
    { key: "name", label: "Name" },
    { key: "relationship", label: "Relationship" },
    { key: "personality", label: "Personality" },
    { key: "context", label: "Context" },
];

const missingFields = computed(() => requiredFields.filter((field) => !form.value[field.key]?.trim()).map((field) => field.label));

const formattedMissingFields = computed(() => {
    const labels = missingFields.value;
    if (labels.length <= 1) {
        return labels.join("");
    }
    return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
});

const isFormValid = computed(() => missingFields.value.length === 0);

const isFormSubmitting = ref(false);
const isTextChat = ref(true);
const inChat = ref(props.inChat);
const recorder = ref(new AudioRecorder());
const { error, isTranscribing, isRecording, isBelowSilenceThreshold, handleRecord, input } = useRecorder({ recorder: recorder.value });
const liveChatError = ref("");

/**M365 Copilot and Outlook (context.app.host.name === "Office" / "Outlook")
 * do not support microphone access so we disable the recording feature.
 */
const recordingEnabled = computedAsync(async () => {
    if (!isInIFrame()) {
        return true;
    }
    app.initialize();
    const context = await app.getContext();
    const hostName = context.app.host.name;
    const micSupported = hostName === "TeamsModern";
    if (!micSupported) {
        // Temporary debugging utility to surface platform mic disablement in Sentry; not a long-term solution.
        logError(`Live Chat disabled: platform "${hostName}" does not support microphone (user_uuid=${userDetails?.value?.uuid})`, {
            tags: {
                reason: "platform_microphone_unsupported",
                host_name: hostName,
                user_uuid: userDetails?.value?.uuid,
                coaching_session_id: coachingSessionId ?? undefined,
            },
        });
    }
    return micSupported;
}, true);

const messageStack = [
    "What are you hoping to get out of this role play?",
    "What about this conversation will be hard?",
    "What do you want to practice handling in this interaction?",
    "Any other context I should know to make this role play useful?",
];

// this is based on # of characters typed
const checkpointsTextChars = [0, 100, 250, 350];
// this is based on time elapsed
const checkpointsVoiceMs = [100, 2000, 4000, 8000];

watch(input, () => {
    form.value.context = input.value;
    fromRecording.value = true;
});

watch(
    form,
    () => {
        liveChatError.value = "";
    },
    { deep: true },
);

watch(
    () => props.errors?.roleplay_setup,
    (roleplaySetupError) => {
        if (roleplaySetupError) {
            liveChatError.value = roleplaySetupError;
            isFormSubmitting.value = false;
        }
    },
    { immediate: true },
);

/**
 * Handle starting a text-based role play
 * Emits: inChatRolePlayStarted with data object containing:
 *   - data.name: Name of the person being role-played
 *   - data.relationship: Relationship to the user
 *   - data.personality: Personality traits
 *   - data.context: Scenario/situation for the role play
 *   - chatType: "text" for text-based role play
 */
const handleStartRoleplay = () => {
    const data = {
        name: form.value.name,
        relationship: form.value.relationship,
        personality: form.value.personality,
        context: form.value.context,
    };

    isFormSubmitting.value = true;
    isTextChat.value = true;
    if (!inChat.value) {
        router.post("/role-play/setup/", data, {
            headers: {
                "X-CSRFToken": getCookie("csrftoken"),
                "Content-Type": "multipart/form-data",
            },
        });
    } else {
        emit("inChatRolePlayStarted", { data, chatType: "text" });
    }
};

/**
 * Handle starting a live (realtime voice) role play
 * Emits: inChatRolePlayStarted with data object containing:
 *   - data.name: Name of the person being role-played
 *   - data.relationship: Relationship to the user
 *   - data.personality: Personality traits
 *   - data.context: Scenario/situation for the role play
 *   - chatType: "realtime" for voice-based role play
 */
const handleStartRoleplayLiveChat = () => {
    const data = {
        name: form.value.name,
        relationship: form.value.relationship,
        personality: form.value.personality,
        context: form.value.context,
    };

    isFormSubmitting.value = true;
    isTextChat.value = false;
    liveChatError.value = "";

    // This creates the new ChatBotStream:
    // When starting from chat, include the session hashid in the URL
    const hashid = coachingSessionId;
    const url = hashid ? `/coach/realtime_chat/${hashid}/` : "/coach/realtime_chat/";
    router.post(url, data, {
        headers: {
            "X-CSRFToken": getCookie("csrftoken"),
        },
        onError: (errors) => {
            liveChatError.value = errors.roleplay_setup || "I can't start a live role play with that setup. Please revise the details and try again.";
            isFormSubmitting.value = false;
        },
        onSuccess: () => {
            // This finishes the role_play_setup action after the setup passes guardrails.
            if (inChat.value) {
                emit("inChatRolePlayStarted", { data, chatType: "realtime" });
            }
        },
    });
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";
.role-play-form-container {
    container-type: inline-size;
    container-name: role-play-form;
}

.role-play-grid {
    grid-template-columns: 1fr;
}

@container role-play-form (min-width: 600px) {
    .role-play-grid {
        grid-template-columns: repeat(2, 1fr);
        @apply gap-10;
    }
}

.background {
    background: radial-gradient(40% 15% at 15% 105%, rgba(255, 197, 61, 0.5) 0%, rgba(255, 197, 61, 0) 100%),
        radial-gradient(40% 15% at 50% 105%, rgba(254, 40, 144, 0.24) 0%, rgba(254, 40, 144, 0) 100%),
        radial-gradient(40% 15% at 85% 105%, rgba(169, 32, 203, 0.24) 0%, rgba(169, 32, 203, 0) 100%);
}

:deep() {
    .contextMeter {
        width: 100%;
    }
}
</style>
